import React, { createContext, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '../components';
import { v4 as uuidv4 } from 'uuid';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { AlertsContainer } from '../components/alert/styles';
export const UIContext = createContext<UIContextProps>({} as UIContextProps);


export interface UIContextProps {
  viewAlert:(alert:AlertProps) => any;
  smallBar: boolean,
  setSmallBar: (is:boolean) => void;
  themeDark: boolean,
  setThemeDark: (is:boolean) => void;
  viewModalComplete:boolean;
  setViewModalComplete:(type:boolean) => void;
  activeAnimationComplete:boolean;
  setActiveAnimationComplete:(type:boolean) => void;
  updateAlertProgress:any;
}

export interface AlertProps {
  show?: boolean;
  severity: 'error' | 'information' | 'warning' | string;
  message?: string;
  deleteItem?:any;
  isDark?:boolean;
  timeout?:any;
  canClose?:boolean,
}
type ContextProps = {
  children?:any
}

const AlertItem = memo(function AlertItem({item, isDark, onDelete}:any) {
  const deleteItem = useCallback(() => onDelete(item.id), [item.id, onDelete])

  return (
    <Alert
      isDark={isDark}
      severity={item.severity}
      message={item.message}
      progress={item.progress}
      timeout={item.timeout}
      canClose={item.canClose}
      show={item.show}
      deleteItem={deleteItem}
    />
  )
})

const UIContextProvider: React.FC<ContextProps> = (props) => {
  const compact = useMediaQuery('(max-width: 999px)', typeof window !== 'undefined' && window.innerWidth < 1000)
  const mobile = useMediaQuery('(max-width: 768px)', typeof window !== 'undefined' && window.innerWidth <= 768)
  const breakpointRef = useRef({
    compact,
    mobile,
  })
  const [smallBar, setSmallBar] = useState<boolean>(compact)
  const [themeDark, setDark] = useState<boolean>(false)
  
  useEffect(() => {
    const nextBreakpoint = {
      compact,
      mobile,
    }
    const previousBreakpoint = breakpointRef.current

    if(nextBreakpoint.mobile !== previousBreakpoint.mobile){
      setSmallBar(nextBreakpoint.mobile ? true : nextBreakpoint.compact)
    } else if(nextBreakpoint.compact !== previousBreakpoint.compact){
      setSmallBar(nextBreakpoint.compact)
    }

    breakpointRef.current = nextBreakpoint
  },[compact, mobile])
  
  const [alerts, setAlerts] = useState<any[]>([])
  const [viewModalComplete, setViewModalComplete] = useState<boolean>(false)
  const [activeAnimationComplete, setActiveAnimationComplete] = useState<boolean>(false)
  
  const viewAlert = useCallback((alert:AlertProps) => {
    let unic:any = uuidv4()
    setAlerts((prev) => {
      return [...prev, {
        severity:alert.severity,
        message:alert.message,
        show: alert.show,
        id: unic,
        timeout:alert.timeout,
        canClose:alert.canClose,
        progress:0
      }]
    })
    return unic
  }, [])
  useLayoutEffect(() => {
    const localTheme = localStorage.getItem('theme') === "true"
    // @ts-ignore
    setDark(localTheme)
  },[])
  const setThemeDark = useCallback((status:boolean) => {
    setDark(status)
    // @ts-ignore
    localStorage.setItem('theme', status)
  }, [])
  const deleteItem = useCallback((id:any) => {
    setAlerts((currentAlerts) => currentAlerts.filter((alert:any) => alert.id !== id))
  }, [])
  const updateAlertProgress = useCallback((uuid:any, progress:any) => {
    setAlerts((currentAlerts) => {
      let changed = false
      const updatedAlerts = currentAlerts.map((alert:any) => {
        if (alert.id !== uuid || alert.progress === progress) {
          return alert
        }

        changed = true
        return {...alert, progress}
      })

      return changed ? updatedAlerts : currentAlerts
    })
  }, [])
  const {
    children
  } = props
  const contextValue = useMemo(() => ({
    viewAlert,
    smallBar,
    setSmallBar,
    themeDark,
    setThemeDark,
    viewModalComplete,
    setViewModalComplete,
    activeAnimationComplete,
    setActiveAnimationComplete,
    updateAlertProgress,
  }), [
    activeAnimationComplete,
    smallBar,
    setThemeDark,
    themeDark,
    updateAlertProgress,
    viewAlert,
    viewModalComplete,
  ])

  return (
    <UIContext.Provider value={contextValue}>
      {children}
      <AlertsContainer>
        {alerts.map((item:any) => (
          <AlertItem
            key={item.id}
            item={item}
            isDark={themeDark}
            onDelete={deleteItem}
          /> 
        ))}
      </AlertsContainer>
    </UIContext.Provider>
  );
};

const useUi = () => {
  const context = React.useContext(UIContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context as UIContextProps;
};

export { UIContextProvider, useUi };
